import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { usePushNotifications } from '../usePushNotifications';
import { useAuth } from '../AuthContext';

const PROMPT_DISMISSED_KEY = 'push_notification_prompt_dismissed';

export default function PushNotificationPrompt() {
  const { token } = useAuth();
  const { isSupported, isSubscribed, permission, loading, subscribe } = usePushNotifications();
  const [open, setOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Only show prompt if:
    // 1. User is logged in
    // 2. Push is supported
    // 3. Not already subscribed
    // 4. Permission not denied
    // 5. User hasn't dismissed the prompt before
    // 6. Haven't already shown this session
    if (
      token &&
      isSupported &&
      !isSubscribed &&
      !loading &&
      permission !== 'denied' &&
      !localStorage.getItem(PROMPT_DISMISSED_KEY) &&
      !hasShown
    ) {
      // Delay showing the prompt a bit so it's not immediately on load
      const timer = setTimeout(() => {
        setOpen(true);
        setHasShown(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [token, isSupported, isSubscribed, loading, permission, hasShown]);

  const handleEnable = async () => {
    await subscribe();
    setOpen(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    setOpen(false);
  };

  const handleLater = () => {
    setOpen(false);
  };

  if (!isSupported) return null;

  return (
    <Dialog open={open} onClose={handleLater} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <NotificationsActiveIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6">Stay in the loop!</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Enable push notifications to get alerted when your buddies catch fish or someone catches nearby – even when the app is closed.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', gap: 1, pb: 3, px: 3 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleEnable}
          sx={{ mb: 0.5 }}
        >
          Enable notifications
        </Button>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            variant="text"
            fullWidth
            onClick={handleLater}
            size="small"
          >
            Maybe later
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={handleDismiss}
            size="small"
            color="inherit"
          >
            Don't ask again
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
